import { Component, forwardRef, Input, Output, EventEmitter, OnInit, HostListener, ElementRef, OnChanges, SimpleChanges, ContentChildren, QueryList, AfterContentInit, ContentChild, ViewChild, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, Injector } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Overlay, ConnectedPosition } from '@angular/cdk/overlay';
import { NxtOptionComponent } from './components/nxt-option/nxt-option.component';
import { NxtOptionGroupComponent } from './components/nxt-option-group/nxt-option-group.component';
import { NxtDropdownTriggerComponent } from './components/nxt-dropdown-trigger/nxt-dropdown-trigger.component';
import { NxtDropdownLabelSuffixDirective } from './nxt-dropdown-label-suffix.directive';
import { NxtDropdownConfig, NxtDropdownOption } from './interfaces/nxt-dropdown.interfaces';
import { isAllSelected, isPartiallySelected, isOptionSelected, updateSelectedOptions, getValuesFromSelectedOptions, toggleOptionSelection } from './utils/selection.utils';
import { getDisplayText, getPendingDisplayText } from './utils/display.utils';
import { filterOptionsBySearch, clearSearchState } from './utils/search.utils';
import { getSanitizedIcon, trackByValue, handleDropdownKeyDown, handleSearchKeyDown } from './utils/ui.utils';

@Component({
  selector: 'nxt-dropdown',
  templateUrl: './nxt-dropdown.component.html',
  styleUrls: ['./nxt-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NxtDropdownComponent),
      multi: true
    }
  ]
})
export class NxtDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, AfterContentInit {
  
  @Input() options: NxtDropdownOption[] = [];

  @Input() config: NxtDropdownConfig = {};

  @Input() compareWith: ((a: unknown, b: unknown) => boolean) | null = null;

  _config: NxtDropdownConfig = {
    placeholder: 'Select an option',
    disabled: false,
    required: false,
    multiple: false,
    selectionLimitEnabled: false,
    selectionLimit: 5,
    confirmation: false,
    requireSelectionToApply: false,
    panelClass: '',
    panelWidth: 'trigger',
    searchable: false,
    searchPlaceholder: 'Search options...',
    minSearchLength: 0,
    label: 'label',
    value: 'value',
    iconType: 'caret',
    infieldLabel: false,
    infieldLabelText: '',
    infieldLabelPosition: 'infield',
    floatlabel: false,
    floatlabelText: '',
    floatlabelPosition: 'infield',
    labelSuffixHtml: '',
    confirmationButtons: {
      apply: {
        text: 'Apply',
        icon: ''
      },
      cancel: {
        text: 'Cancel',
        icon: ''
      }
    },
    selectAllPosition: 'below',
    virtualScroll: {
      enabled: false,
      itemSize: 40,
      minBufferPx: 100,
      maxBufferPx: 200,
      viewportHeight: 200
    }
  }
  
  @Output() selectionChange = new EventEmitter<any>();

  @ContentChildren(NxtOptionComponent) optionComponents?: QueryList<NxtOptionComponent>;
  
  @ContentChildren(NxtOptionGroupComponent) optionGroupComponents?: QueryList<NxtOptionGroupComponent>;
  
  @ContentChild(NxtDropdownTriggerComponent) customTrigger?: NxtDropdownTriggerComponent;

  @ContentChild(NxtDropdownLabelSuffixDirective) labelSuffixRef?: NxtDropdownLabelSuffixDirective;

  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;
  
  @ViewChild('dropdownTrigger', { static: false }) dropdownTrigger!: ElementRef<HTMLElement>;
  
  @ViewChild('dropdownPanel', { static: false }) dropdownPanel!: ElementRef<HTMLElement>;
  
  @ViewChild(CdkVirtualScrollViewport, { static: false }) virtualScrollViewport?: CdkVirtualScrollViewport;

  value: any;

  /** Disabled by the parent FormControl / NgControl (via setDisabledState). */
  private _controlDisabledByForm = false;

  isDisabled: boolean = false;
  
  isOpen: boolean = false;
  
  selectedOptions: NxtDropdownOption[] = [];
  
  pendingOptions: NxtDropdownOption[] = [];
  
  private pendingValue: any = null;
  
  searchText: string = '';
  
  filteredOptions: NxtDropdownOption[] = [];
  
  showSearchInput: boolean = false;
  
  shouldPositionAbove: boolean = false;
  
  // CDK Overlay positioning and sizing
  overlayPositions: ConnectedPosition[] = [
    // Prefer left-aligned, but fall back to right-aligned when near viewport edge
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 2 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 2 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -2 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -2 }
  ];
  triggerWidth: number = 0;
  overlayWidth: number | undefined = undefined;

  private onChange = (value: any) => {};
  
  private onTouched = () => {
    this._isTouched = true;
  };

  // Internal touched state for standalone dropdowns (without ngModel/formControl)
  private _isTouched: boolean = false;

  private _ngControl: NgControl | null = null;
  private _ngControlChecked = false;

  constructor(
    private elementRef: ElementRef,
    private sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private overlay: Overlay,
    private injector: Injector
  ) {}

  private get ngControl(): NgControl | null {
    if (!this._ngControlChecked) {
      this._ngControlChecked = true;
      try {
        this._ngControl = this.injector.get(NgControl, null);
      } catch {
        this._ngControl = null;
      }
    }
    return this._ngControl;
  }

  private requestChangeDetection(): void {
    this.changeDetectorRef.markForCheck();
  }
  
  private valuesEqual(a: unknown, b: unknown): boolean {
    return this.compareWith ? this.compareWith(a, b) : a === b;
  }
  
  get repositionScroll() {
    return this.overlay.scrollStrategies.reposition();
  }

  get isRequired(): boolean {
    return this._config.required || this.ngControl?.control?.hasValidator(Validators.required) || false;
  }

  get isRequiredInvalid(): boolean {
    if (!this.isRequired) return false;
    
    // Only show required error if touched or dirty
    const control = this.ngControl?.control;
    if (control) {
      // When using formControl or ngModel, check control's touched/dirty state
      if (!control.touched && !control.dirty) {
        return false;
      }
    } else {
      // For standalone dropdowns (no formControl/ngModel), use internal touched state
      if (!this._isTouched) {
        return false;
      }
    }
    
    const hasValue = this._config.multiple 
      ? Array.isArray(this.value) && this.value.length > 0 
      : this.value !== null && this.value !== undefined && this.value !== '';
    return !hasValue;
  }

  private resetPendingOptions(): void {
    if (this._config.confirmation && this._config.multiple) {
      this.pendingOptions = [...this.selectedOptions];
      if (this.isSelectionLimitActive() && this.pendingOptions.length > this.effectiveSelectionLimit) {
        this.pendingOptions = this.pendingOptions.slice(0, this.effectiveSelectionLimit);
      }
    }
  }

  private updateInternalState(): void {
    this.applyEffectiveDisabled();
    this.updateSelectedOptions();
    this.updateFilteredOptions();
  }

  /** Effective disabled = form/ControlValueAccessor disabled OR config.disabled (config alone must not be cleared by setDisabledState(false)). */
  private applyEffectiveDisabled(): void {
    this.isDisabled = this._controlDisabledByForm || !!this._config.disabled;
    this.updateTriggerProperties();
  }

  private checkAndHandlePendingValue(): void {
    if (this.options && this.options.length > 0 && this.pendingValue !== null) {
      this.handlePendingValue();
    }
  }

  ngOnInit() {
    this.updateConfiguration();
    
    this.updateInternalState();
    
    this.resetPendingOptions();
  }

  ngAfterContentInit() {
    // Process immediately - @ContentChildren is resolved before this lifecycle hook
    // The changes subscription below handles dynamic updates (e.g., *ngFor with async data)
    this.processContentProjectedOptions();
    
    if (this.optionComponents) {
      this.optionComponents.changes.subscribe(() => {
        this.processContentProjectedOptions();
      });
    }
    
    if (this.optionGroupComponents) {
      this.optionGroupComponents.changes.subscribe(() => {
        this.processContentProjectedOptions();
      });
    }

    this.setupCustomTrigger();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      this._config = { ...this._config, ...changes['config'].currentValue };
    }
    
    // When bound options input changes (e.g., async load), update internal state
    if (changes['options']) {
      this.remapOptionsIfNeeded();
      this.updateFilteredOptions();
      this.checkAndHandlePendingValue();
      this.updateSelectedOptions();
    }
    
    const configProperties = ['config', 'placeholder', 'disabled', 'required', 
      'multiple', 'confirmation', 'panelClass', 'searchable', 'searchPlaceholder', 
      'minSearchLength', 'iconType', 'infieldLabel', 'infieldLabelText', 'infieldLabelPosition', 
      'floatlabel', 'floatlabelText', 'floatlabelPosition', 'labelSuffixHtml', 'strictConfigMode'];
    
    const hasConfigChanges = configProperties.some(prop => changes[prop]);
    
    if (hasConfigChanges) {
      this.updateConfiguration();
      this.updateInternalState();
    }
    
    if (changes['optionComponents'] || changes['optionGroupComponents']) {
      this.processContentProjectedOptions();
    }
    
    this.requestChangeDetection();
  }

  private processContentProjectedOptions(): void {
    const hasDirectOptions = this.optionComponents && this.optionComponents.length > 0;
    const hasGroupedOptions = this.optionGroupComponents && this.optionGroupComponents.length > 0;
    
    if (hasDirectOptions || hasGroupedOptions) {
      const projectedOptions: NxtDropdownOption[] = [];
      
      if (hasDirectOptions) {
        this.optionComponents!.forEach(optionComp => {
          projectedOptions.push({
            value: optionComp.value,
            label: optionComp.option.label,
            // store TemplateRef on a non-serialized field for runtime rendering
            // @ts-ignore - extended at runtime
            templateRef: optionComp.getTemplateRef(),
            disabled: optionComp.disabled
          } as any);
        });
      }
      
      if (hasGroupedOptions) {
        this.optionGroupComponents!.forEach(groupComp => {
          const groupOptions = groupComp.getOptions();
          projectedOptions.push(...groupOptions);
        });
      }
      
      this.options = projectedOptions;
      this.updateFilteredOptions();
      
      this.checkAndHandlePendingValue();
    }
    
    this.requestChangeDetection();
  }

  private setupCustomTrigger(): void {
    if (!this.customTrigger) return;
    
    this.customTrigger.triggerClick.subscribe((event: Event) => {
      this.toggleDropdown();
    });

    this.customTrigger.keyDown.subscribe((event: KeyboardEvent) => {
      this.onKeyDown(event);
    });

    this.updateTriggerProperties();
  }

  private updateTriggerProperties(): void {
    if (!this.customTrigger) return;
    
    this.customTrigger.disabled = this.isDisabled;
    this.customTrigger.isOpen = this.isOpen;
    this.customTrigger.required = this.isRequiredInvalid;
    this.customTrigger.multiple = this._config.multiple;
    this.customTrigger.placeholder = this._config.placeholder;
    this.customTrigger.iconType = this._config.iconType;
  }

  private updateConfiguration(): void {
    this.remapOptionsIfNeeded();

    this.checkAndHandlePendingValue();
  }

  private isNxtDropdownOption(item: any): item is NxtDropdownOption {
    return item && typeof item === 'object' && 'label' in item && 'value' in item;
  }

  private remapOptionsIfNeeded(): void {
    if (!Array.isArray(this.options) || this.options.length === 0) {
      return;
    }

    const labelKey = this._config.label ?? 'label';
    const valueKey = this._config.value ?? 'value';

    // If options already in expected shape, only keep as-is when mapping keys are defaults.
    // If consumer requested a different mapping (e.g., value: 'id'), force remap.
    const allAlreadyMapped = this.options.every(opt => this.isNxtDropdownOption(opt));
    const shouldForceRemap = (labelKey !== 'label' || valueKey !== 'value');
    if (allAlreadyMapped && !shouldForceRemap) {
      return;
    }

    const mapped: NxtDropdownOption[] = (this.options as any[]).map((item: any) => {
      const alreadyOption = this.isNxtDropdownOption(item);
      // When a custom mapping is requested, do not preserve existing value/label as-is.
      // Always derive using the configured keys so selection compares against the intended field.
      if (!shouldForceRemap && alreadyOption) {
        return item as NxtDropdownOption;
      }

      const mappedValue = item?.[valueKey] ?? (alreadyOption ? (item as NxtDropdownOption).value : item?.value) ?? item;
      const mappedLabel = item?.[labelKey] ?? (alreadyOption ? (item as NxtDropdownOption).label : item?.label) ?? String(mappedValue ?? '');

      const option: NxtDropdownOption = {
        value: mappedValue,
        label: mappedLabel,
        disabled: item?.disabled ?? (alreadyOption ? (item as NxtDropdownOption).disabled : false),
        group: item?.group ?? (alreadyOption ? (item as NxtDropdownOption).group : undefined)
      } as NxtDropdownOption;
      return option;
    });

    this.options = mapped;
  }

  private handlePendingValue(): void {
    this.value = this.pendingValue;
    this.updateSelectedOptions();
    
    this.resetPendingOptions();
    
    this.pendingValue = null;
  }

  get hasCustomTrigger(): boolean {
    return !!this.customTrigger;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement | null;
    if (this.isOpen && target && target.closest('.nxt-dropdown-overlay-panel')) {
      // Click occurred inside the overlay panel; ignore
      return;
    }
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
  
  @HostListener('document:keydown.escape', ['$event'])
  onEscKeydown(event: KeyboardEvent): void {
    if (this.isOpen) {
      event.stopPropagation();
      this.closeDropdown();
    }
  }

  private getTriggerElement(): HTMLElement | null {
    if (this.hasCustomTrigger) {
      const customTriggerElement = this.elementRef.nativeElement.querySelector('nxt-dropdown-trigger');
      return customTriggerElement as HTMLElement;
    }
    
    return this.dropdownTrigger?.nativeElement || null;
  }

  private focusSearchInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  private updateDropdownPosition(): void {
    if (this.isOpen && this.dropdownPanel?.nativeElement) {
      this.calculateDropdownPosition();
      
      this.ensurePanelInViewport();
    }
  }

  private ensurePanelInViewport(): void {
    if (!this.dropdownPanel?.nativeElement) return;

    const panel = this.dropdownPanel.nativeElement;
    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    panel.style.transform = '';
    
    if (panelRect.right > viewportWidth) {
      const overflowX = panelRect.right - viewportWidth;
      panel.style.transform = `translateX(-${overflowX + 10}px)`;
    } else if (panelRect.left < 0) {
      const overflowX = Math.abs(panelRect.left);
      panel.style.transform = `translateX(${overflowX + 10}px)`;
    }
    
    if (this.shouldPositionAbove && panelRect.top < 0) {
      const overflowY = Math.abs(panelRect.top);
      const currentTransform = panel.style.transform;
      panel.style.transform = `${currentTransform} translateY(${overflowY + 10}px)`;
    }
  }

  clearSearchInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.focus();
    }
  }

  getSearchInputValue(): string {
    return this.searchInput?.nativeElement?.value || '';
  }

  setSearchInputValue(value: string): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = value;
    }
  }

  writeValue(value: any): void {
    this.value = value;
    
    if (!this.options || this.options.length === 0) {
      this.pendingValue = value;
      return;
    }
    
    this.pendingValue = null;
    
    this.updateSelectedOptions();
    
    this.resetPendingOptions();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = () => {
      this._isTouched = true;
      fn();
    };
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabledByForm = isDisabled;
    this.applyEffectiveDisabled();
    this.requestChangeDetection();
  }

  toggleDropdown(): void {
    if (!this.isDisabled) {
      this.isOpen = !this.isOpen;
      this.updateTriggerProperties();
      
      if (this.isOpen) {
        // Sync overlay width to trigger width
        this.triggerWidth = this.dropdownTrigger?.nativeElement?.offsetWidth ?? 0;
        this.overlayWidth = this.computeOverlayWidth();
        
        this.resetPendingOptions();
        
        if (this._config.searchable) {
          this.showSearchInput = true;
          setTimeout(() => {
            this.focusSearchInput();
            this.requestChangeDetection();
          }, 100);
        }
        
        // Scroll to selected option if virtual scrolling is enabled
        this.scrollToSelectedOptionOnOpen();
      } else {
        // Mark as touched when dropdown closes (blur behavior)
        this.onTouched();
        this.clearSearch();
      }
      
      this.requestChangeDetection();
    }
  }

  private scrollToSelectedOptionOnOpen(): void {
    if (this.shouldEnableVirtualScroll() && this.selectedOptions.length > 0) {
      // Use setTimeout to ensure the virtual scroll viewport is rendered
      setTimeout(() => {
        this.scrollToSelectedOption();
        this.requestChangeDetection();
      }, 100);
    }
  }

  private calculateDropdownPosition(): void {
    const triggerElement = this.getTriggerElement();
    if (!triggerElement) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = this._config.confirmation ? 300 : 200;
    const buffer = 50;
    
    const spaceBelow = viewportHeight - triggerRect.bottom;
    
    this.shouldPositionAbove = spaceBelow < (dropdownMaxHeight + buffer);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      // Update width if bound to trigger width
      this.triggerWidth = this.dropdownTrigger?.nativeElement?.offsetWidth ?? this.triggerWidth;
      this.overlayWidth = this.computeOverlayWidth();
      this.requestChangeDetection();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isOpen) {
      // Update width if bound to trigger width (handles sticky headers/layout shifts)
      this.triggerWidth = this.dropdownTrigger?.nativeElement?.offsetWidth ?? this.triggerWidth;
      this.overlayWidth = this.computeOverlayWidth();
      this.requestChangeDetection();
    }
  }

  closeDropdown(): void {
    const wasOpen = this.isOpen;
    this.isOpen = false;
    this.updateTriggerProperties();
    if (wasOpen) {
      this.onTouched();
    }
    this.clearSearch();
    
    this.resetPendingOptions();
    this.requestChangeDetection();
  }
  
  onOverlayDetach(): void {
    // Overlay closed (e.g., due to scroll strategy or backdrop)
    this.isOpen = false;
    this.updateTriggerProperties();
    this.onTouched();
    this.requestChangeDetection();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchText = target.value;
    this.updateFilteredOptions();
    this.requestChangeDetection();
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    handleSearchKeyDown(event, {
      closeDropdown: () => this.closeDropdown(),
      selectFirstOption: () => {
        const firstOption = this.filteredOptions.find(opt => !opt.disabled);
        if (firstOption) {
          this.selectOption(firstOption);
        }
      }
    });
  }

  clearSearch(): void {
    const clearedState = clearSearchState();
    this.searchText = clearedState.searchText;
    this.showSearchInput = clearedState.showSearchInput;
    this.updateFilteredOptions();
    this.clearSearchInput();
    this.requestChangeDetection();
  }

  clearSearchText(): void {
    this.searchText = '';
    this.updateFilteredOptions();
    this.clearSearchInput();
    this.requestChangeDetection();
  }

  updateFilteredOptions(): void {
    this.filteredOptions = filterOptionsBySearch(this.options, this.searchText, this._config.searchable, this._config.minSearchLength);
    
    if (this.isOpen) {
      // Use requestAnimationFrame for layout calculations - runs before next paint
      requestAnimationFrame(() => this.calculateDropdownPosition());
    }
    
    this.requestChangeDetection();
  }

  selectOption(option: NxtDropdownOption): void {
    if (this.isOptionRuntimeDisabled(option)) return;

    if (this._config.multiple) {
      if (this._config.confirmation) {
        this.togglePendingSelection(option);
      } else {
        this.toggleMultipleSelection(option);
      }
    } else {
      this.selectSingleOption(option);
    }
    this.requestChangeDetection();
  }

  selectAll(): void {
    if (!this._config.multiple) return;

    // When selection limit is active, do not allow select-all
    if (this.isSelectionLimitActive()) return;

    if (this._config.confirmation) {
      this.togglePendingSelectAll();
    } else {
      this.toggleSelectAll();
    }
  }

  private toggleSelectAll(): void {
    const availableOptions = this.options.filter(option => !option.disabled);
    const allSelected = availableOptions.every(option =>
      this.selectedOptions.some(selected => this.valuesEqual(selected.value, option.value))
    );

    if (allSelected) {
      this.selectedOptions = [];
      this.value = [];
    } else {
      this.selectedOptions = [...availableOptions];
      this.value = availableOptions.map(opt => opt.value);
    }

    this.updateFilteredOptions();

    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
    this.requestChangeDetection();
  }

  private togglePendingSelectAll(): void {
    // When selection limit is active, do not allow select-all
    if (this.isSelectionLimitActive()) return;

    const availableOptions = this.options.filter(option => !option.disabled);
    const allSelected = availableOptions.every(option =>
      this.pendingOptions.some(selected => this.valuesEqual(selected.value, option.value))
    );

    if (allSelected) {
      this.pendingOptions = [];
    } else {
      this.pendingOptions = [...availableOptions];
    }
    this.requestChangeDetection();
  }

  private togglePendingSelection(option: NxtDropdownOption): void {
    const index = this.pendingOptions.findIndex(opt => this.valuesEqual(opt.value, option.value));
    
    if (index > -1) {
      this.pendingOptions.splice(index, 1);
    } else {
      // Enforce selection limit in confirmation mode
      if (this.isSelectionLimitActive() && this.pendingOptions.length >= this.effectiveSelectionLimit) {
        return;
      }
      this.pendingOptions.push(option);
    }
    this.requestChangeDetection();
  }

  isAllSelected(): boolean {
    return isAllSelected(
      this.filteredOptions,
      this._config.confirmation ? this.pendingOptions : this.selectedOptions,
      this._config.multiple,
      this.compareWith || undefined
    );
  }

  isPartiallySelected(): boolean {
    return isPartiallySelected(
      this.filteredOptions,
      this._config.confirmation ? this.pendingOptions : this.selectedOptions,
      this._config.multiple,
      this.compareWith || undefined
    );
  }

  isAllAvailableSelected(): boolean {
    const availableOptions = this.options.filter(option => !option.disabled);
    return availableOptions.every(option =>
      (this._config.confirmation ? this.pendingOptions : this.selectedOptions)
        .some(selected => this.valuesEqual(selected.value, option.value))
    );
  }

  isPartiallyAvailableSelected(): boolean {
    const availableOptions = this.options.filter(option => !option.disabled);
    const selectedOptions = this._config.confirmation ? this.pendingOptions : this.selectedOptions;
    const selectedCount = availableOptions.filter(option =>
      selectedOptions.some(selected => this.valuesEqual(selected.value, option.value))
    ).length;
    return selectedCount > 0 && selectedCount < availableOptions.length;
  }

  isOptionSelected(option: NxtDropdownOption): boolean {
    const optionsToCheck = this._config.confirmation && this._config.multiple ? this.pendingOptions : this.selectedOptions;
    return isOptionSelected(option, optionsToCheck, this.compareWith || undefined);
  }

  private selectSingleOption(option: NxtDropdownOption): void {
    this.value = option.value;
    this.selectedOptions = [option];
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
    this.closeDropdown();
    this.requestChangeDetection();
  }

  private toggleMultipleSelection(option: NxtDropdownOption): void {
    const isAlreadySelected = this.selectedOptions.some(opt => this.valuesEqual(opt.value, option.value));
    if (!isAlreadySelected && this.isSelectionLimitActive() && this.selectedOptions.length >= this.effectiveSelectionLimit) {
      return;
    }

    this.selectedOptions = toggleOptionSelection(option, this.selectedOptions, this.compareWith || undefined);
    this.value = getValuesFromSelectedOptions(this.selectedOptions, this._config.multiple);
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
    this.requestChangeDetection();
  }

  private updateSelectedOptions(): void {
    this.selectedOptions = updateSelectedOptions(this.value, this.options, this._config.multiple, this.compareWith || undefined);

    // Clamp to selection limit if active
    if (this.isSelectionLimitActive() && Array.isArray(this.selectedOptions) && this.selectedOptions.length > this.effectiveSelectionLimit) {
      this.selectedOptions = this.selectedOptions.slice(0, this.effectiveSelectionLimit);
    }

    if (this._config.multiple) {
      this.value = getValuesFromSelectedOptions(this.selectedOptions, this._config.multiple);
    }
    this.requestChangeDetection();
  }

  getDisplayText(): string {
    return getDisplayText(this.selectedOptions, this._config.placeholder, this._config.multiple);
  }

  getPendingDisplayText(): string {
    return getPendingDisplayText(this.pendingOptions, this._config.placeholder, this._config.multiple);
  }

  getInfieldLabelText(): string {
    return this._config.infieldLabelText || this._config.placeholder;
  }

  getFloatLabelText(): string {
    return this._config.floatlabelText || this._config.placeholder;
  }

  onKeyDown(event: KeyboardEvent): void {
    handleDropdownKeyDown(event, this.isDisabled, this.isOpen, {
      toggleDropdown: () => this.toggleDropdown(),
      closeDropdown: () => this.closeDropdown()
    });
  }

  trackByValue(index: number, option: NxtDropdownOption): any {
    return trackByValue(index, option);
  }

  /**
   * Stable row identity for panel lists. Plain trackByValue(option.value) can collide
   * (e.g. duplicate values) and reuse the wrong DOM node next to group headers.
   */
  trackByFilteredOption(index: number, option: NxtDropdownOption): string {
    const g = option.group != null ? String(option.group) : '';
    const v =
      option.value !== null && option.value !== undefined
        ? typeof option.value === 'object'
          ? JSON.stringify(option.value)
          : String(option.value)
        : '';
    const lbl = option.label != null ? String(option.label) : '';
    return `${g}::${v}::${lbl}::${index}`;
  }

  shouldShowGroupHeader(option: NxtDropdownOption, index: number): boolean {
    if (!option.group) {
      return false;
    }
    
    for (let i = 0; i < index; i++) {
      if (this.filteredOptions[i].group === option.group) {
        return false;
      }
    }
    
    return true;
  }

  applySelection(): void {
    if (!this._config.confirmation || !this._config.multiple) return;

    // Prevent applying when configuration requires at least one selection
    if (this.shouldDisableApplyButton()) {
      return;
    }

    // Clamp pending to limit if needed
    if (this.isSelectionLimitActive() && this.pendingOptions.length > this.effectiveSelectionLimit) {
      this.pendingOptions = this.pendingOptions.slice(0, this.effectiveSelectionLimit);
    }

    this.selectedOptions = [...this.pendingOptions];
    this.value = getValuesFromSelectedOptions(this.selectedOptions, this._config.multiple);
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
    this.closeDropdown();
    this.requestChangeDetection();
  }

  private shouldDisableApplyButton(): boolean {
    if (!this._config.confirmation || !this._config.multiple) {
      return false;
    }
    if (!this._config.requireSelectionToApply) {
      return false;
    }
    return this.pendingOptions.length === 0;
  }

  cancelSelection(): void {
    if (!this._config.confirmation || !this._config.multiple) return;

    this.resetPendingOptions();
    this.closeDropdown();
    this.requestChangeDetection();
  }

  getSanitizedIcon(icon: string): SafeHtml {
    return getSanitizedIcon(icon, this.sanitizer);
  }

  // Virtual scrolling helper methods
  get isVirtualScrollEnabled(): boolean {
    return this._config.virtualScroll?.enabled === true;
  }

  get virtualScrollItemSize(): number {
    return this._config.virtualScroll?.itemSize || 40;
  }

  get virtualScrollMinBufferPx(): number {
    return this._config.virtualScroll?.minBufferPx || 100;
  }

  get virtualScrollMaxBufferPx(): number {
    return this._config.virtualScroll?.maxBufferPx || 200;
  }

  get virtualScrollViewportHeight(): number {
    return this._config.virtualScroll?.viewportHeight || 200;
  }

  // Selection limit helpers
  private isSelectionLimitActive(): boolean {
    return this._config.multiple === true && this._config.selectionLimitEnabled === true;
  }

  private get effectiveSelectionLimit(): number {
    const provided = this._config.selectionLimit;
    return (typeof provided === 'number' && provided > 0) ? provided : 5;
  }

  private hasSelectionCapacity(list: NxtDropdownOption[]): boolean {
    return list.length < this.effectiveSelectionLimit;
  }

  shouldShowSelectAll(): boolean {
    return this._config.multiple === true && this._config.selectionLimitEnabled !== true;
    }

  isOptionRuntimeDisabled(option: NxtDropdownOption): boolean {
    if (option?.disabled) return true;
    if (!this.isSelectionLimitActive()) return false;
    const optionsToCheck = this._config.confirmation && this._config.multiple ? this.pendingOptions : this.selectedOptions;
    const alreadySelected = optionsToCheck.some(selected => this.valuesEqual(selected.value, option.value));
    return !alreadySelected && !this.hasSelectionCapacity(optionsToCheck);
  }

  shouldEnableVirtualScroll(): boolean {
    return this.isVirtualScrollEnabled && this.filteredOptions.length > 50;
  }

  onVirtualScrollIndexChanged(event: any): void {
    // Handle virtual scroll index changes if needed
    // This can be used for performance monitoring or additional logic
  }

  scrollToOption(option: NxtDropdownOption): void {
    if (!this.shouldEnableVirtualScroll() || !this.virtualScrollViewport) {
      return;
    }

    const index = this.filteredOptions.findIndex(opt => opt.value === option.value);
    if (index !== -1) {
      this.virtualScrollViewport.scrollToIndex(index);
    }
  }

  scrollToSelectedOption(): void {
    if (!this.shouldEnableVirtualScroll() || this.selectedOptions.length === 0) {
      return;
    }

    const firstSelected = this.selectedOptions[0];
    this.scrollToOption(firstSelected);
  }
  
  // ========== Group selection helpers ==========
  onGroupHeaderClick(groupName: string, event: Event): void {
    if (!this._config.multiple) {
      return;
    }
    this.toggleGroupSelection(groupName);
    event.stopPropagation();
  }

  private getGroupAvailableOptions(groupName: string): NxtDropdownOption[] {
    return this.options.filter(opt => opt.group === groupName && !opt.disabled);
  }

  isGroupAllSelected(groupName: string): boolean {
    if (!this._config.multiple) {
      return false;
    }
    const listToCheck = this._config.confirmation ? this.pendingOptions : this.selectedOptions;
    const groupAvailable = this.getGroupAvailableOptions(groupName);
    if (groupAvailable.length === 0) {
      return false;
    }
    return groupAvailable.every(groupOpt => listToCheck.some(sel => this.valuesEqual(sel.value, groupOpt.value)));
  }

  isGroupPartiallySelected(groupName: string): boolean {
    if (!this._config.multiple) {
      return false;
    }
    const listToCheck = this._config.confirmation ? this.pendingOptions : this.selectedOptions;
    const groupAvailable = this.getGroupAvailableOptions(groupName);
    if (groupAvailable.length === 0) {
      return false;
    }
    const selectedCount = groupAvailable.filter(groupOpt => listToCheck.some(sel => this.valuesEqual(sel.value, groupOpt.value))).length;
    return selectedCount > 0 && selectedCount < groupAvailable.length;
  }

  toggleGroupSelection(groupName: string): void {
    if (!this._config.multiple) {
      return;
    }
    // Align with global select-all rule: do not allow group-select when selection limit is active
    if (this.isSelectionLimitActive()) {
      return;
    }

    const optionsForGroup = this.getGroupAvailableOptions(groupName).filter(opt => !this.isOptionRuntimeDisabled(opt));
    if (optionsForGroup.length === 0) {
      return;
    }

    if (this._config.confirmation) {
      const allSelected = optionsForGroup.every(groupOpt => this.pendingOptions.some(sel => this.valuesEqual(sel.value, groupOpt.value)));
      if (allSelected) {
        this.pendingOptions = this.pendingOptions.filter(sel =>
          !optionsForGroup.some(groupOpt => this.valuesEqual(groupOpt.value, sel.value))
        );
      } else {
        const next = [...this.pendingOptions];
        optionsForGroup.forEach(groupOpt => {
          const exists = next.some(sel => this.valuesEqual(sel.value, groupOpt.value));
          if (!exists) {
            next.push(groupOpt);
          }
        });
        this.pendingOptions = next;
      }
      this.requestChangeDetection();
      return;
    }

    const allSelected = optionsForGroup.every(groupOpt => this.selectedOptions.some(sel => this.valuesEqual(sel.value, groupOpt.value)));
    if (allSelected) {
      this.selectedOptions = this.selectedOptions.filter(sel =>
        !optionsForGroup.some(groupOpt => this.valuesEqual(groupOpt.value, sel.value))
      );
    } else {
      const next = [...this.selectedOptions];
      optionsForGroup.forEach(groupOpt => {
        const exists = next.some(sel => this.valuesEqual(sel.value, groupOpt.value));
        if (!exists) {
          next.push(groupOpt);
        }
      });
      this.selectedOptions = next;
    }
    this.value = getValuesFromSelectedOptions(this.selectedOptions, this._config.multiple);
    this.updateFilteredOptions();
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
    this.requestChangeDetection();
  }
  
  private computeOverlayWidth(): number | undefined {
    const widthConfig = this._config.panelWidth;
    if (typeof widthConfig === 'number' && widthConfig > 0) {
      return widthConfig;
    }
    if (widthConfig === 'auto' || widthConfig === null) {
      return undefined; // let content define width
    }
    // default and 'trigger'
    return this.triggerWidth || undefined;
  }
} 